import { i18nService, type LanguageType } from '../services/i18n';

const TECHNICAL_ERROR_PATTERNS = [
  /\b(?:error|http(?:\/\d(?:\.\d)?)?)\s*[:#-]?\s*[1-5]\d{2}\b/i,
  /^\s*[1-5]\d{2}(?:\s+\S.*)?$/i,
  /\b(?:status|statuscode|code|errcode)\s*[:=]?\s*-?\d+\b/i,
  /\b(?:ENOENT|EACCES|EPERM|ECONNREFUSED|ECONNRESET|ETIMEDOUT|ERR_[A-Z0-9_]+)\b/,
  /\bhttps?:\/\/\S+/i,
  /(?:^|\s)(?:\/[^/\s]+){2,}(?:\s|$)/,
  /(?:^|\s)[A-Za-z]:\\(?:[^\\\s]+\\)+/i,
];

const LANGUAGE_SCRIPT_PATTERNS: Partial<Record<LanguageType, RegExp>> = {
  zh: /[㐀-鿿]/,
  'zh-TW': /[㐀-鿿]/,
  en: /^[\x00-\x7f]*$/,
};

export const isTechnicalErrorMessage = (message: string): boolean =>
  TECHNICAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));

const interpolate = (
  template: string,
  vars?: Record<string, string | number>,
): string => {
  let result = template;
  for (const [name, value] of Object.entries(vars ?? {})) {
    result = result.split(`{${name}}`).join(String(value));
  }
  return result;
};

/**
 * 将用户可见的描述性错误切换为当前界面语言。
 *
 * HTTP 状态、Error 404、系统错误码、URL 与绝对路径等技术错误保持原文，
 * 便于用户反馈和开发排查；已经是当前语言的服务端错误也保留原文。
 */
export const localizeUserFacingErrorText = (
  rawError: unknown,
  fallback: string,
): string => {
  const raw = rawError instanceof Error
    ? rawError.message.trim()
    : typeof rawError === 'string'
      ? rawError.trim()
      : '';

  if (!raw) return fallback;
  if (isTechnicalErrorMessage(raw)) return raw;

  const language = i18nService.getLanguage();
  const scriptPattern = LANGUAGE_SCRIPT_PATTERNS[language];
  if (scriptPattern?.test(raw)) return raw;

  return fallback;
};

export const localizeUserFacingError = (
  rawError: unknown,
  fallbackKey: string,
  vars?: Record<string, string | number>,
): string => localizeUserFacingErrorText(
  rawError,
  interpolate(i18nService.t(fallbackKey), vars),
);
